import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, query, getDocs, doc, deleteDoc, where } from 'firebase/firestore'; // removed getDoc as unused
import { db, DEFAULT_STORE_ID } from '../../../firebase/firebase';
import { getProductById } from '../../../firebase/product-functions';
import { useUser } from '../../../context/UserContext';
import { useLanguage } from '../../../context/LanguageContext';
import { useStoreSettings } from '../../../hooks/useStoreSettings';
import useCurrency from '../../../hooks/useCurrency';
import './WishlistPage.css';
import ProductGrid from "../../../components/common/ProductGrid/ProductGrid";

const WishlistPage = () => {
    const { currentUser } = useUser();
    const { language } = useLanguage();
    const { formatDisplay } = useCurrency();
    const navigate = useNavigate();

    // Translations for wishlist page
    const translations = {
        en: {
            wishlist: "Wishlist",
            pleaseSignIn: "Please sign in to view your wishlist",
            signIn: "Sign In",
            loading: "Loading your wishlist...",
            wishlistEmpty: "Your wishlist is empty",
            discoverMore: "Discover more",
            unavailableProducts: "Unavailable Products",
            productNoLongerAvailable: "This product is no longer available",
            remove: "Remove",
            usingLocallyStored: "Using locally stored wishlist due to connection issues.",
            failedToRemove: "Failed to remove item from wishlist",
            failedToAdd: "Failed to add item to cart"
        },
        ar: {
            wishlist: "قائمة الأمنيات",
            pleaseSignIn: "يرجى تسجيل الدخول لعرض قائمة أمنياتك",
            signIn: "تسجيل الدخول",
            loading: "جاري تحميل قائمة أمنياتك...",
            wishlistEmpty: "قائمة أمنياتك فارغة",
            discoverMore: "اكتشف المزيد",
            unavailableProducts: "منتجات غير متوفرة",
            productNoLongerAvailable: "هذا المنتج لم يعد متوفراً",
            remove: "إزالة",
            usingLocallyStored: "استخدام قائمة الأمنيات المحفوظة محلياً بسبب مشاكل الاتصال.",
            failedToRemove: "فشل في إزالة العنصر من قائمة الأمنيات",
            failedToAdd: "فشل في إضافة العنصر إلى السلة"
        }
    };

    const t = translations[language] || translations.en;

    // Get store settings for currency
    const { currency: storeCurrency } = useStoreSettings(DEFAULT_STORE_ID); // unused but keeping for future use

    const [wishlistItems, setWishlistItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [useLocalFallback, setUseLocalFallback] = useState(false);

    useEffect(() => {
        // Check if user is logged in and active store is set
        if (!currentUser || !DEFAULT_STORE_ID) {
            console.log("active store id:", DEFAULT_STORE_ID)
            setLoading(false);
            return;
        }

        const fetchWishlist = async () => {
            setLoading(true);
            setError(null);

            try {
                // Fetch from Firestore - get all wishlist items for this user
                const wishlistRef = collection(db, 'users', currentUser.uid, 'wishlist');

                // Filter wishlist by the active store ID
                const q = query(wishlistRef, where("storeId", "==", DEFAULT_STORE_ID));
                const querySnapshot = await getDocs(q);

                const items = [];
                for (const docSnapshot of querySnapshot.docs) {
                    const wishlistItem = docSnapshot.data();

                    try {
                        // Always fetch fresh product data using getProductById
                        if (wishlistItem.productId) {
                            try {
                                // Fetch fresh product data from the store
                                const freshProductData = await getProductById(DEFAULT_STORE_ID, wishlistItem.productId);

                                items.push({
                                    id: docSnapshot.id,
                                    ...wishlistItem,
                                    productData: freshProductData // Fresh data with current stock status, prices, etc.
                                });
                            } catch (productErr) {
                                console.error("Error fetching fresh product data:", productErr);
                                // Product not found - mark as unavailable
                                items.push({
                                    id: docSnapshot.id,
                                    ...wishlistItem,
                                    productNotFound: true
                                });
                            }
                        } else {
                            // No productId - invalid wishlist item
                            items.push({
                                id: docSnapshot.id,
                                ...wishlistItem,
                                processingError: true
                            });
                        }
                    } catch (itemErr) {
                        console.error("Error processing wishlist item:", itemErr);
                        items.push({
                            id: docSnapshot.id,
                            ...wishlistItem,
                            processingError: true
                        });
                    }
                }

                setWishlistItems(items);
                setUseLocalFallback(false);
            } catch (err) {
                console.error("Error fetching wishlist from Firestore:", err);

                // If Firebase permissions fail, try fallback to localStorage but still filter by store
                if (err.message && err.message.includes("permission")) {
                    try {
                        console.log("Using localStorage fallback for wishlist");
                        setUseLocalFallback(true);

                        // Try to get wishlist from localStorage
                        const allLocalWishlist = JSON.parse(localStorage.getItem(`wishlist_${currentUser.uid}`)) || [];

                        // Filter local wishlist by active store
                        const localWishlist = allLocalWishlist.filter(item => item.storeId === DEFAULT_STORE_ID);

                        // Process local wishlist items - fetch fresh product data
                        const processedItems = await Promise.all(localWishlist.map(async (item) => {
                            // Always fetch fresh product data using getProductById
                            if (item.productId) {
                                try {
                                    const freshProductData = await getProductById(DEFAULT_STORE_ID, item.productId);
                                    return {
                                        ...item,
                                        productData: freshProductData // Fresh data with current stock, prices, etc.
                                    };
                                } catch (productErr) {
                                    console.error("Error fetching fresh product data for local item:", productErr);
                                    return {
                                        ...item,
                                        productNotFound: true
                                    };
                                }
                            }

                            // If no product ID, mark as error
                            return {
                                ...item,
                                processingError: true
                            };
                        }));

                        setWishlistItems(processedItems);
                    } catch (localErr) {
                        console.error("Error fetching wishlist from localStorage:", localErr);
                        setError("Failed to load your wishlist. Please try again later.");
                    }
                } else {
                    setError("Failed to load your wishlist. Please try again later.");
                }
            } finally {
                setLoading(false);
            }
        };

        fetchWishlist();
    }, [currentUser, DEFAULT_STORE_ID]);

    const handleRemoveFromWishlist = async (wishlistItemId) => {
        try {
            if (useLocalFallback) {
                // Remove from localStorage
                const localWishlist = JSON.parse(localStorage.getItem(`wishlist_${currentUser.uid}`)) || [];
                const updatedWishlist = localWishlist.filter(item => item.id !== wishlistItemId);
                localStorage.setItem(`wishlist_${currentUser.uid}`, JSON.stringify(updatedWishlist));
            } else {
                // Remove from Firestore
                await deleteDoc(doc(db, 'users', currentUser.uid, 'wishlist', wishlistItemId));
            }

            // Update state to remove the item
            setWishlistItems(prev => prev.filter(item => item.id !== wishlistItemId));
        } catch (err) {
            console.error("Error removing from wishlist:", err);
            setError(t.failedToRemove);

            // If Firestore fails, still update the UI
            if (err.message && err.message.includes("permission")) {
                setWishlistItems(prev => prev.filter(item => item.id !== wishlistItemId));
            }
        }
    };
    // Transform wishlist items to product format for ProductGrid
    const transformedProducts = wishlistItems
        .filter(item => item.productData && !item.productNotFound && !item.productError && !item.processingError)
        .map(item => ({
            ...item.productData,
            wishlistItemId: item.id // Include for removal
        }));

    // Format currency (legacy function for backward compatibility)


    // Handle product click - navigate to product page
    const handleProductClick = (product) => {
        navigate(`/product/${product.id}`);
    };

    if (!currentUser) {
        return (
            <div className="wishlist-container">
                <div className="empty-wishlist modern">
                    <h1 className="wishlist-title">{t.wishlist}</h1>
                    <p className="wishlist-empty-text">{t.pleaseSignIn}</p>
                    <button className="discover-more-btn" onClick={() => navigate('/login')}>
                        {t.signIn}
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="wishlist-container">
            {/* Wishlist Header */}
            {transformedProducts.length > 0 && (
                <div className="wishlist-header">
                    <h1 className="wishlist-title">{t.wishlist}</h1>
                    {useLocalFallback && (
                        <div className="fallback-notice">
                            <p>{t.usingLocallyStored}</p>
                        </div>
                    )}
                </div>
            )}

            {/* Product Grid */}
            <ProductGrid
                products={transformedProducts}
                loading={loading}
                error={error}
                onProductClick={handleProductClick}
                formatCurrency={formatDisplay}
                gridColumns={4}
                cardVariant="wishlist"
                loadingText={t.loading}
                emptyStateText={
                    !currentUser ? t.pleaseSignIn : t.wishlistEmpty
                }
                emptyStateAction={{
                    text: !currentUser ? t.signIn : t.discoverMore,
                    onClick: () => navigate(!currentUser ? '/login' : '/shop')
                }}
                className="wishlist-grid"
            />

            {/* Error State for Unavailable Products */}
            {wishlistItems.some(item => item.productNotFound || item.productError || item.processingError) && (
                <div className="unavailable-products">
                    <h3>{t.unavailableProducts}</h3>
                    <div className="unavailable-grid">
                        {wishlistItems
                            .filter(item => item.productNotFound || item.productError || item.processingError)
                            .map(item => (
                                <div key={item.id} className="wishlist-item">
                                    <div className="product-unavailable">
                                        <div className="unavailable-icon">!</div>
                                        <p>{t.productNoLongerAvailable}</p>
                                        <button onClick={() => handleRemoveFromWishlist(item.id)}>
                                            {t.remove}
                                        </button>
                                    </div>
                                </div>
                            ))
                        }
                    </div>
                </div>
            )}
        </div>
    );
};

export default WishlistPage;